import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Icon from 'common/components/Elements/Icon/Icon';
import useShortcut from 'common/utilities/hooks/useShortcut/useShortcut';
import useUpDownList from 'common/utilities/hooks/useUpDownList/useUpDownList';
import CommandLinkContent from 'core/components/Elements/Command/CommandLinkContent/CommandLinkContent';
import { getDropdownItemForCommand, popperjsMaxSizeModifiers } from 'core/components/Elements/DropDown/DropDown';
import type { EnhancedCommand } from 'core/utilities/core.types';
import type {
  CSSProperties, RefObject} from 'react';
import React, { useCallback, useEffect, useState,
} from 'react';

import classes from './ItemNestedMenu.style';

interface ComponentProps {
  item: EnhancedCommand;
  buttonRef: React.RefObject<HTMLElement>;
  onNestedMenuToggle: (toggle: boolean) => void;
  onClickMenuItem?: () => void;
  selected?: boolean;
}

function ItemNestedMenu({
  item, onClickMenuItem, selected, onNestedMenuToggle, buttonRef,
}: ComponentProps) {
  const { id, commands, toggleButton } = item;
  const nestedMenuRef = React.createRef<HTMLButtonElement>();
  const menuListRef = React.createRef<HTMLUListElement>();
  const [toggle, setToggle] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLButtonElement>(null);

  const { active: selectedItem, reset: resetSelectedItem } = useUpDownList(
    commands.length,
    !toggle,
    commands.map((c, i) => (!c.active ? i : undefined)).filter((c) => c !== undefined) as number[],
  );

  const selectItemTrigger = useShortcut('Enter', true, { disabled: !toggle, debounceTime: 0 });

  useEffect(() => {
    if (selectItemTrigger && selectedItem !== -1) {
      (buttonRef?.current as HTMLButtonElement).blur();

      (menuListRef.current
        ?.querySelector(`li:nth-of-type(${selectedItem + 1})`)
        ?.querySelector(':first-child') as HTMLElement)
        ?.click();
    }
  }, [buttonRef, menuListRef, selectItemTrigger, selectedItem]);

  const handleClose = useCallback(() => {
    setToggle(false);
    onNestedMenuToggle(false);
    resetSelectedItem();
  }, [onNestedMenuToggle, resetSelectedItem]);

  const handleToggle = useCallback(() => {
    if (!anchorEl) setAnchorEl(nestedMenuRef.current);

    if (toggle) {
      handleClose();
    } else {
      onNestedMenuToggle(true);
      setToggle(true);
    }
  }, [anchorEl, handleClose, nestedMenuRef, onNestedMenuToggle, toggle]);

  const handleCloseEvent = (event: React.MouseEvent | React.TouchEvent, ref: RefObject<HTMLElement>) => {
    if (ref.current && ref.current.contains(event.target as Document | HTMLElement)) {
      return;
    }

    handleClose();
  };

  const menuItems = commands.map((c, i) => {
    const command: EnhancedCommand = { ...c };
    if (toggleButton) {
      command.onClick = item.onClick;
    }

    return (
      getDropdownItemForCommand(command, selectedItem === i, nestedMenuRef, handleCloseEvent, () => {}, () => {})
    );
  });

  const arrowRight = useShortcut('ArrowRight', true, { debounceTime: 0 });

  useEffect(() => {
    if (selected && arrowRight && !toggle) {
      handleToggle();
    }
  }, [arrowRight, handleToggle, selected, toggle]);

  const arrowLeft = useShortcut('ArrowLeft', true);

  useEffect(() => {
    if (selected && arrowLeft && toggle) {
      handleToggle();
    }
  }, [arrowLeft, handleToggle, selected, toggle]);

  /** Disabled when closed or when a nested menu is open */
  const escapeShortcutTrigger = useShortcut('Escape', true, { disabled: !toggle });

  useEffect(() => {
    if (escapeShortcutTrigger) {
      handleClose();
    }
  }, [escapeShortcutTrigger, handleClose]);

  const dropdownStyle: CSSProperties = {
    zIndex: 1302, // above Dropdown zIndex
    overflowX: 'hidden',
  };

  return (
    <li
      data-testid="nestedMenu"
      role="menuitem"
      aria-haspopup="menu"
      aria-owns={toggle ? 'menu-list-grow' : undefined}
      onMouseEnter={handleToggle}
      onMouseLeave={handleToggle}
      data-e2e-dropdown-nested-item={id}
    >
      <MenuItem
        ref={nestedMenuRef}
        component="button"
        role="button"
        dense
        sx={classes.item}
        selected={selected}
      >
        <CommandLinkContent command={item} />
        <Icon element={KeyboardArrowRightIcon} ml pullRight />
      </MenuItem>

      {toggle && (
        <Popper
          open
          placement="right-start"
          anchorEl={anchorEl}
          style={dropdownStyle}
          onClick={(e) => handleCloseEvent(e, nestedMenuRef)}
          modifiers={[
            {
              name: 'computeStyles',
              options: {
                gpuAcceleration: false, // needed to avoid blurry content when zooming with the browser
              },
            },
            {
              name: 'offset',
              options: {
                offset: [-8, 0],
              },
            },
            ...popperjsMaxSizeModifiers,
          ]}
        >
          <Paper sx={classes.paper}>
            <ClickAwayListener
              onClickAway={(e) => (
                handleCloseEvent(e as unknown as React.MouseEvent | React.TouchEvent, nestedMenuRef)
              )}
            >
              <MenuList
                data-testid="nestedMenuItems"
                onClick={() => { if (onClickMenuItem) onClickMenuItem(); }}
                dense
                ref={menuListRef}
              >
                {menuItems}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Popper>
      )}
    </li>
  );
}

export default ItemNestedMenu;
